<template>
  <div class="build-lab-page">
    <section class="build-lab-intro">
      <div>
        <span class="eyebrow">{{ $t('forge.build.eyebrow') }}</span>
        <h1>{{ $t('forge.build.title') }}</h1>
        <p>{{ $t('forge.build.intro') }}</p>
      </div>
      <div class="build-side-switch">
        <button type="button" :class="{ 'is-active': side === 'survivor' }" @click="setSide('survivor')">{{ $t('forge.common.survivor') }}</button>
        <button type="button" :class="{ 'is-active': side === 'killer' }" @click="setSide('killer')">{{ $t('forge.common.killer') }}</button>
      </div>
    </section>

    <SurvivorBestBuild
      v-if="side === 'survivor'"
      :lang="lang"
      :color="color"
      :sids="sids"
      :kids="kids"
      :perksSHD="perksSHD"
    />

    <section v-else class="killer-build-lab">
      <div class="killer-build-controls">
        <label>
          <span>{{ $t('forge.build.killer') }}</span>
          <select v-model.number="killerId" @change="onKillerChange">
            <option v-for="killer in killers" :key="killer.id" :value="killer.id">{{ killerName(killer) }}</option>
          </select>
        </label>
        <div class="strategy-switch" role="group" :aria-label="$t('forge.build.strategy')">
          <button v-for="option in strategies" :key="option.id" type="button" :class="{ 'is-active': strategy === option.id }" @click="setStrategy(option.id)">
            <small>{{ option.kicker }}</small>
            <strong>{{ option.label }}</strong>
          </button>
        </div>
        <button type="button" class="random-killer" @click="randomKiller">{{ $t('forge.build.randomKiller') }}</button>
      </div>

      <div class="killer-builder-toolbar">
        <label class="central-perk-control">
          <span>{{ $t('forge.build.centralPerk') }}</span>
          <select :value="centralIsManual ? centralPerkId : ''" @change="onCentralChange">
            <option value="">{{ automaticCentralLabel }}</option>
            <option v-for="perk in centralPerkOptions" :key="perk.id" :value="perk.id">{{ perk.displayName }}</option>
          </select>
        </label>
        <button type="button" class="toolbar-button" @click="randomCentral">{{ $t('forge.build.randomCentral') }}</button>
        <div class="mode-switch" role="group" :aria-label="$t('forge.build.buildMode')">
          <button v-for="option in modeOptions" :key="option.id" type="button" :class="{ 'is-active': buildMode === option.id }" @click="setBuildMode(option.id)">
            <small>{{ option.kicker }}</small>
            <strong>{{ option.label }}</strong>
          </button>
        </div>
        <button type="button" class="toolbar-button toolbar-button--reset" @click="resetLocks">{{ $t('forge.build.resetLocks') }}</button>
      </div>

      <section class="killer-gameplan-picker">
        <header>
          <div><span class="eyebrow">{{ gameplanHeading }}</span><strong>{{ selectedGameplan.title }}</strong></div>
          <p>{{ selectedGameplan.summary }}</p>
        </header>
        <div role="group" :aria-label="gameplanHeading">
          <button
            v-for="(gameplan, index) in killerGameplans"
            :key="gameplan.id"
            type="button"
            :class="{ 'is-active': variantIndex === index }"
            @click="selectKillerVariant(index)"
          >
            <strong>{{ gameplan.title }}</strong>
            <small>{{ gameplan.short }}</small>
          </button>
        </div>
      </section>

      <div class="killer-action-hub">
        <div class="killer-recommendations">
          <div><span class="eyebrow">{{ $t('forge.build.recommendedAnchors') }}</span><small>{{ $t('forge.build.recommendedAnchorsText') }}</small></div>
          <button v-for="entry in recommendedCentrals" :key="entry.perk.id" type="button" :class="{ 'is-active': centralIsManual && entry.perk.id === centralPerkId }" @click="setRecommendedCentral(entry.perk.id)">
            <span>{{ entry.perk.displayName }}</span><strong>{{ entry.score }}</strong>
          </button>
        </div>
        <div class="killer-quick-actions">
          <button type="button" @click="generateVariant">{{ $t('forge.build.generateVariant') }}</button>
          <button type="button" @click="optimizeBuild">{{ $t('forge.build.optimizeDiversity') }}</button>
          <button type="button" class="is-primary" @click="copyKillerBuild">{{ copyStatus || $t('forge.build.copyBuild') }}</button>
        </div>
      </div>

      <div class="killer-build-summary">
        <div class="killer-build-summary__identity" :style="{ '--killer-accent': bestBuild.profile.accent }">
          <span class="killer-monogram"><img v-if="currentKillerIcon" :src="currentKillerIcon" :alt="currentKillerName" decoding="async"><b v-else>{{ killerInitials }}</b></span>
          <div>
            <small>{{ profileLabel }}</small>
            <h2>{{ currentKillerName }}</h2>
            <p>{{ currentKillerPower || $t('forge.roulette.uniquePower') }}</p>
          </div>
        </div>
        <div class="killer-build-summary__score">
          <span>{{ $t('forge.build.fitScore') }}</span>
          <strong>{{ bestBuild.score }}</strong>
          <small>/100</small>
        </div>
        <div class="killer-build-summary__copy">
            <p>{{ selectedGameplan.summary }}</p>
          <div class="summary-chips">
            <span>{{ selectedMode.label }}</span>
            <span>{{ bestBuild.variant.title }}</span>
            <span>{{ bestBuild.coverage.roles.length }} {{ $t('forge.build.rolesCovered') }}</span>
            <span>{{ activeLockCount }}/3 {{ $t('forge.build.locks') }}</span>
            <span>{{ bestBuild.diagnostics.diversity }}% {{ $t('forge.build.diversity') }}</span>
          </div>
          <small class="killer-build-summary__tradeoff">
            + {{ bestBuild.variant.advantages[0] }} &middot; &minus; {{ bestBuild.variant.drawbacks[0] }}
          </small>
        </div>
      </div>

      <div class="killer-build-grid">
        <article
          v-for="slot in bestBuild.slots"
          :key="slot.slot + '-' + slot.perk.id"
          :class="['killer-build-card', { 'is-central': slot.slot === 1, 'is-locked': isSlotLocked(slot.slot) }]"
        >
          <div class="killer-build-card__number">{{ String(slot.slot).padStart(2, '0') }}</div>
          <div class="killer-build-card__badges">
            <span>{{ relationshipLabel(slot) }}</span>
            <button v-if="slot.slot > 1" type="button" :class="{ 'is-active': isSlotLocked(slot.slot) }" @click="toggleSlotLock(slot)">
              {{ isSlotLocked(slot.slot) ? $t('forge.build.locked') : $t('forge.build.lockSlot') }}
            </button>
          </div>
          <div class="killer-build-card__icon"><PerkIcon :perk="slot.perk" side="killer" /></div>
          <div class="killer-build-card__body">
            <h3>{{ slot.perk.displayName }}</h3>
            <p>{{ slot.perk.tags.slice(0, 4).join(' · ') || $t('forge.roulette.adaptiveRole') }}</p>
            <div class="killer-build-card__reason"><span>{{ $t('forge.build.whyItFits') }}</span><strong>{{ slotReason(slot) }}</strong></div>
          </div>
          <div class="killer-build-card__fit">
            <span>{{ $t('forge.build.slotFit') }}</span>
            <strong>{{ slot.score }}</strong>
          </div>
          <div class="killer-build-card__meter"><span :style="{ width: slot.score + '%' }"></span></div>
          <div v-if="slot.slot > 1 && slot.alternatives.length" class="quick-swaps">
            <span>{{ $t('forge.build.quickSwaps') }}</span>
            <button v-for="alternative in quickAlternatives(slot)" :key="alternative.perk.id" type="button" :title="alternative.compatibility && alternative.compatibility.reason" @click="selectAlternative(slot.slot, alternative.perk.id)">
              <span>{{ alternative.perk.displayName }}</span><strong>{{ alternative.score }}</strong>
            </button>
          </div>
          <details v-if="slot.alternatives.length">
            <summary>{{ $t('forge.build.alternatives') }} <b>{{ slot.alternatives.length }}</b></summary>
            <label class="alternative-search">
              <span>{{ $t('forge.build.searchPerks') }}</span>
              <input type="search" :value="slotSearchQuery(slot.slot)" :placeholder="$t('forge.build.searchPlaceholder')" @input="setSlotSearchQuery(slot.slot, $event.target.value)">
            </label>
            <div class="alternative-list">
              <button v-for="alternative in visibleAlternatives(slot)" :key="alternative.perk.id" type="button" @click="selectAlternative(slot.slot, alternative.perk.id)">
                <span>{{ alternative.perk.displayName }}</span><strong>{{ alternative.score }}</strong>
              </button>
            </div>
          </details>
        </article>
      </div>

      <div class="killer-build-flow" :aria-label="$t('forge.build.buildFlow')">
        <article v-for="slot in bestBuild.slots" :key="'flow-' + slot.slot">
          <small>{{ String(slot.slot).padStart(2, '0') }} · {{ relationshipLabel(slot) }}</small>
          <strong>{{ slot.perk.displayName }}</strong>
          <span>{{ roleLabel(slot.primaryRole) }}</span>
        </article>
      </div>

      <section class="killer-diagnostics">
        <header>
          <div><span class="eyebrow">{{ $t('forge.build.liveDiagnostics') }}</span><h3>{{ $t('forge.build.liveDiagnosticsTitle') }}</h3></div>
          <p>{{ $t('forge.build.liveDiagnosticsText') }}</p>
        </header>
        <div class="diagnostic-grid">
          <article v-for="metric in diagnosticCards" :key="metric.id">
            <div><span>{{ metric.label }}</span><strong>{{ metric.value }}</strong></div>
            <div class="diagnostic-meter"><span :style="{ width: metric.value + '%' }"></span></div>
            <small>{{ metric.help }}</small>
          </article>
        </div>
      </section>

      <section class="killer-build-debrief">
        <header><div><span class="eyebrow">{{ $t('forge.build.currentBuild') }}</span><h3>{{ currentKillerName }} · {{ selectedMode.label }}</h3></div><strong>{{ bestBuild.score }}/100</strong></header>
        <div>
          <article v-for="slot in bestBuild.slots" :key="'debrief-' + slot.slot">
            <small>{{ relationshipLabel(slot) }}</small>
            <h4>{{ slot.perk.displayName }}</h4>
            <p>{{ slotReason(slot) }}</p>
            <span>{{ slot.perk.tags.slice(0, 5).join(' · ') }}</span>
          </article>
        </div>
      </section>

      <div class="killer-analysis-grid">
        <article>
          <span class="eyebrow">{{ $t('forge.build.currentLogic') }}</span>
          <h3>{{ selectedMode.label }}</h3>
          <p>{{ selectedMode.description }}</p>
        </article>
        <article>
          <span class="eyebrow">{{ $t('forge.build.roleCoverage') }}</span>
          <h3>{{ roleCoverageLine }}</h3>
          <p>{{ $t('forge.build.coverageDynamicText', { count: killerCatalog.length }) }}</p>
        </article>
        <article :class="{ 'has-warning': bestBuild.warnings.length }">
          <span class="eyebrow">{{ $t('forge.build.guardrail') }}</span>
          <h3>{{ bestBuild.warnings.length ? $t('forge.build.warningDetected') : $t('forge.build.noRedundancy') }}</h3>
          <p>{{ buildWarningText }}</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import PerkIcon from '@/components/PerkIcon.vue'
import SurvivorBestBuild from '@/views/SurvivorBestBuild.vue'
import wikiData from '@/data/wikiLoadoutData'
import gameIcons from '@/data/gameIconManifest.json'
import { createKillerBestBuild, createKillerCatalog, KILLER_BUILD_VARIANTS } from '@/lib/loadoutEngine'
import { localizedCharacter } from '@/lib/loadoutLocalization'

const KILLER_MODES = ['loadout_craft', 'synergy_focus']

const FRENCH_GAMEPLANS = {
  balanced_toolkit: { title: 'Boîte à outils équilibrée', short: 'Info · tempo · chase', summary: 'Une information, un outil de tempo et une conversion de chase, sans empiler les ralentissements.' },
  generator_control: { title: 'Contrôle des générateurs', short: 'Deux anti-gen maximum', summary: 'Une route de ralentissement assumée, limitée à deux perks générateur et complétée par de la conversion.' },
  chase_conversion: { title: 'Conversion de chase', short: 'Boucles · coups · tracking', summary: 'Deux outils de chase distincts, puis une information permettant d’enchaîner rapidement la cible suivante.' },
  aura_hunter: { title: 'Chasseur d’auras', short: 'Lecture · extension · chase', summary: 'Des lectures d’aura temporisées transformées en acquisition de cible et en chases répétées.' },
  hex_network: { title: 'Réseau de maléfices', short: 'Payoff · protection · punition', summary: 'Un vrai package de totems avec payoff, protection ou reconstruction et conversion de la pression.' },
  endgame_trap: { title: 'Piège de fin de partie', short: 'Portes · délai · menace', summary: 'Un build qui accepte des slots plus faibles au début pour rendre la phase des portes réellement dangereuse.' },
  stealth_obsession: { title: 'Furtivité et obsession', short: 'Embuscade · rotation · chase', summary: 'Manipule le rayon de terreur et l’Obsession afin de créer des fenêtres d’embuscade.' },
  injury_pressure: { title: 'Pression sur les soins', short: 'Blessure · détection · snowball', summary: 'Maintient plusieurs survivants blessés et punit les fenêtres de soin, sans dépendre d’un package anti-gen.' }
}

export default {
  name: 'BuildLab',
  components: { PerkIcon, SurvivorBestBuild },
  props: {
    lang: { type: String, required: true },
    color: { type: Boolean, default: false },
    sids: { type: Array, default: () => [] },
    kids: { type: Array, default: () => [] },
    perksKHD: { type: Object, required: true },
    perksSHD: { type: Object, required: true }
  },
  data () {
    const requestedKillerId = Number(this.$route.query.killer)
    const requestedGameplanIndex = KILLER_BUILD_VARIANTS.findIndex(gameplan => gameplan.id === this.$route.query.killerPlan)
    return {
      side: this.$route.query.side === 'survivor' ? 'survivor' : 'killer',
      killerId: wikiData.killers.some(killer => killer.id === requestedKillerId) ? requestedKillerId : 1,
      strategy: ['balanced', 'aggression', 'control'].indexOf(this.$route.query.strategy) !== -1 ? this.$route.query.strategy : 'balanced',
      buildMode: KILLER_MODES.indexOf(this.$route.query.killerMode) !== -1 ? this.$route.query.killerMode : 'loadout_craft',
      centralPerkId: this.$route.query.killerCentral || '',
      centralIsManual: Boolean(this.$route.query.killerCentral),
      variantIndex: requestedGameplanIndex >= 0 ? requestedGameplanIndex : 0,
      copyStatus: '',
      killers: wikiData.killers,
      killerCatalog: [],
      slotLocks: {
        2: { enabled: false, perkId: null },
        3: { enabled: false, perkId: null },
        4: { enabled: false, perkId: null }
      },
      slotSearchQueries: { 2: '', 3: '', 4: '' }
    }
  },
  computed: {
    killerGameplans () {
      const french = String(this.$i18n.locale || '').toLowerCase().indexOf('fr') === 0
      return KILLER_BUILD_VARIANTS.map(gameplan => {
        const localized = french && FRENCH_GAMEPLANS[gameplan.id]
        return Object.assign({}, gameplan, localized || {}, {
          short: (localized && localized.short) || gameplan.preferredTypes.slice(0, 3).join(' · ')
        })
      })
    },
    selectedGameplan () {
      return this.killerGameplans[this.variantIndex] || this.killerGameplans[0]
    },
    gameplanHeading () {
      return String(this.$i18n.locale || '').toLowerCase().indexOf('fr') === 0 ? 'Plan de jeu' : 'Gameplay plan'
    },
    strategies () {
      return [
        { id: 'balanced', kicker: this.$t('forge.build.complete'), label: this.$t('forge.build.balanced') },
        { id: 'aggression', kicker: this.$t('forge.build.fast'), label: this.$t('forge.build.aggression') },
        { id: 'control', kicker: this.$t('forge.build.methodical'), label: this.$t('forge.build.control') }
      ]
    },
    modeOptions () {
      return [
        { id: 'loadout_craft', kicker: this.$t('forge.build.completeBuild'), label: this.$t('forge.build.loadoutCraft'), description: this.$t('forge.build.loadoutCraftText') },
        { id: 'synergy_focus', kicker: this.$t('forge.build.pushCombo'), label: this.$t('forge.build.synergyFocus'), description: this.$t('forge.build.synergyFocusText') }
      ]
    },
    selectedMode () {
      return this.modeOptions.find(option => option.id === this.buildMode) || this.modeOptions[0]
    },
    currentKiller () {
      return this.killers.find(killer => killer.id === this.killerId) || this.killers[0]
    },
    centralPerkOptions () {
      return this.killerCatalog.slice().sort((first, second) => first.displayName.localeCompare(second.displayName))
    },
    automaticCentralLabel () {
      const central = this.killerCatalog.find(perk => perk.id === this.centralPerkId)
      return central ? `Auto \u00b7 ${central.displayName}` : 'Auto'
    },
    bestBuild () {
      return createKillerBestBuild(this.currentKiller, this.killerCatalog, {
        strategy: this.strategy,
        mode: this.buildMode,
        centralPerkId: this.centralIsManual ? this.centralPerkId : null,
        lockedSlots: this.slotLocks,
        variantIndex: this.variantIndex
      })
    },
    currentKillerDetails () {
      return localizedCharacter(this.currentKiller, 'killer', this.$i18n.locale)
    },
    currentKillerName () {
      return this.currentKillerDetails.displayName
    },
    currentKillerPower () {
      return this.currentKillerDetails.power
    },
    profileLabel () {
      return this.profileText('label')
    },
    profileSummary () {
      return this.profileText('summary')
    },
    killerInitials () {
      return this.currentKillerName.split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    currentKillerIcon () {
      return gameIcons.characters.killer[String(this.currentKiller.id)] || ''
    },
    activeLockCount () {
      return [2, 3, 4].filter(slot => this.isSlotLocked(slot)).length
    },
    recommendedCentrals () {
      return (this.bestBuild.centralRecommendations || []).slice(0, 4)
    },
    diagnosticCards () {
      return [
        { id: 'central', value: this.bestBuild.diagnostics.centralFit, label: this.$t('forge.build.centralFitMetric'), help: this.$t('forge.build.centralFitHelp') },
        { id: 'power', value: this.bestBuild.diagnostics.powerFit, label: this.$t('forge.build.powerFitMetric'), help: this.$t('forge.build.powerFitHelp') },
        { id: 'synergy', value: this.bestBuild.diagnostics.synergy, label: this.$t('forge.build.synergyMetric'), help: this.$t('forge.build.synergyHelp') },
        { id: 'diversity', value: this.bestBuild.diagnostics.diversity, label: this.$t('forge.build.diversityMetric'), help: this.$t('forge.build.diversityHelp') }
      ]
    },
    roleCoverageLine () {
      const roles = this.bestBuild.coverage.roles
      if (!roles.length) return this.$t('forge.build.flexibleCoverage')
      return roles.map(role => this.roleLabel(role)).join(' · ')
    },
    buildWarningText () {
      const warning = this.bestBuild.warnings[0]
      if (!warning) return this.$t('forge.build.noRedundancyText')
      if (warning.type === 'missingArchetypeSlots') return `${warning.count} gameplay requirement${warning.count > 1 ? 's are' : ' is'} still missing.`
      if (warning.type === 'generatorOverflow') return `This plan allows fewer Generator perks than the ${warning.count} currently selected.`
      if (warning.type === 'centralOutsideArchetype') return 'The manually selected central perk sits outside this gameplay plan; the other slots compensate for it.'
      return this.$t('forge.build.repeatedRoleText', { role: this.roleLabel(warning.role), count: warning.count })
    }
  },
  watch: {
    '$i18n.locale': function () {
      this.createCatalog()
      this.ensureCentral()
    }
  },
  created () {
    this.createCatalog()
    this.ensureCentral()
  },
  methods: {
    killerName (killer) {
      return localizedCharacter(killer, 'killer', this.$i18n.locale).displayName
    },
    profileText (field) {
      const key = `forge.profiles.${this.bestBuild.profile.id}.${field}`
      return this.$te(key) ? this.$t(key) : this.bestBuild.profile[field]
    },
    createCatalog () {
      this.killerCatalog = createKillerCatalog(Object.keys(this.perksKHD.frames), frameName => this.$t(`perks.killer.${frameName}`))
    },
    ensureCentral () {
      const centralExists = this.killerCatalog.some(perk => perk.id === this.centralPerkId)
      if (this.centralIsManual && centralExists) return
      if (this.centralIsManual) this.centralIsManual = false
      const build = createKillerBestBuild(this.currentKiller, this.killerCatalog, {
        strategy: this.strategy,
        mode: this.buildMode,
        variantIndex: this.variantIndex
      })
      this.centralPerkId = build.perks[0] ? build.perks[0].id : ''
    },
    setSide (side) {
      if (side === this.side) return
      this.side = side
      this.syncQuery()
    },
    setStrategy (strategy) {
      this.strategy = strategy
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    setBuildMode (mode) {
      if (KILLER_MODES.indexOf(mode) === -1 || mode === this.buildMode) return
      this.buildMode = mode
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    onKillerChange () {
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    onCentralChange (event) {
      const perkId = event && event.target ? event.target.value : ''
      this.resetLocks(false)
      this.centralIsManual = Boolean(perkId)
      if (perkId) this.centralPerkId = perkId
      else this.ensureCentral()
      this.copyStatus = ''
      this.syncQuery()
    },
    syncQuery () {
      const query = Object.assign({}, this.$route.query, {
        side: this.side,
        killer: String(this.killerId),
        strategy: this.strategy,
        killerMode: this.buildMode,
        killerPlan: this.selectedGameplan.id
      })
      if (this.centralIsManual && this.centralPerkId) query.killerCentral = this.centralPerkId
      else delete query.killerCentral
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    randomKiller () {
      const pool = this.killers.filter(killer => killer.id !== this.killerId)
      this.killerId = pool[Math.floor(Math.random() * pool.length)].id
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    randomCentral () {
      const currentCentral = this.killerCatalog.find(perk => perk.id === this.centralPerkId)
      const pool = this.killerCatalog.filter(perk => {
        return !currentCentral || perk.gameplayId !== currentCentral.gameplayId
      })
      if (!pool.length) return
      this.centralPerkId = pool[Math.floor(Math.random() * pool.length)].id
      this.centralIsManual = true
      this.copyStatus = ''
      this.resetLocks(false)
      this.syncQuery()
    },
    resetLocks (sync = true) {
      const slots = [2, 3, 4]
      slots.forEach(slot => {
        this.$set(this.slotLocks, slot, { enabled: false, perkId: null })
        this.$set(this.slotSearchQueries, slot, '')
      })
      if (sync) this.syncQuery()
    },
    isSlotLocked (slot) {
      return Boolean(this.slotLocks[slot] && this.slotLocks[slot].enabled)
    },
    toggleSlotLock (slot) {
      if (slot.slot === 1) return
      const enabled = !this.isSlotLocked(slot.slot)
      this.$set(this.slotLocks, slot.slot, { enabled, perkId: enabled ? slot.perk.id : null })
    },
    selectAlternative (slot, perkId) {
      this.$set(this.slotLocks, slot, { enabled: true, perkId })
      this.$set(this.slotSearchQueries, slot, '')
    },
    setRecommendedCentral (perkId) {
      if (perkId === this.centralPerkId && this.centralIsManual) return
      this.centralPerkId = perkId
      this.centralIsManual = true
      this.copyStatus = ''
      this.resetLocks(false)
      this.syncQuery()
    },
    generateVariant () {
      this.variantIndex = (this.variantIndex + 1) % KILLER_BUILD_VARIANTS.length
      this.copyStatus = ''
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    selectKillerVariant (variantIndex) {
      if (variantIndex === this.variantIndex || !KILLER_BUILD_VARIANTS[variantIndex]) return
      this.variantIndex = variantIndex
      this.copyStatus = ''
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    optimizeBuild () {
      this.buildMode = 'loadout_craft'
      this.variantIndex = 0
      this.copyStatus = ''
      this.resetLocks(false)
      this.ensureCentral()
      this.syncQuery()
    },
    quickAlternatives (slot) {
      return (slot.alternatives || []).slice(0, 3)
    },
    relationshipLabel (slot) {
      const labels = {
        central: this.$t('forge.build.centralSlot'),
        synergy: this.$t('forge.build.directSynergy'),
        power: this.$t('forge.build.powerCoverage'),
        coverage: this.$t('forge.build.flexCoverage')
      }
      if (slot.slot !== 1 && slot.archetypeJob) {
        return slot.archetypeJob
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
      return labels[slot.relationship] || this.roleLabel(slot.primaryRole)
    },
    slotReason (slot) {
      if (slot.compatibility && slot.compatibility.reason) {
        return `${slot.compatibility.confidenceLabel} · ${slot.compatibility.reason}`
      }
      const role = this.roleLabel(slot.primaryRole)
      if (slot.relationship === 'central') return this.$t('forge.build.centralReason', { role })
      if (slot.relationship === 'synergy' && slot.sharedTags) return this.$t('forge.build.directSynergyReason', { count: slot.sharedTags })
      if (slot.relationship === 'synergy') return this.$t('forge.build.synergyBridgeReason', { role })
      if (slot.relationship === 'power') return this.$t('forge.build.powerReason', { profile: this.profileLabel, role })
      return this.$t('forge.build.coverageReason', { role })
    },
    copyKillerBuild () {
      const perkLines = this.bestBuild.slots.map(slot => `${slot.slot}. ${slot.perk.displayName} — ${this.relationshipLabel(slot)}`)
      const text = [`${this.currentKillerName} — ${this.currentKillerPower || this.profileLabel}`]
        .concat(perkLines)
        .concat([`${this.$t('forge.build.fitScore')}: ${this.bestBuild.score}/100`, window.location.href])
        .join('\n')
      const copied = () => { this.copyStatus = this.$t('forge.roulette.copied') }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(copied)
        return
      }
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      document.body.removeChild(area)
      copied()
    },
    slotSearchQuery (slot) {
      return this.slotSearchQueries[slot] || ''
    },
    setSlotSearchQuery (slot, value) {
      this.$set(this.slotSearchQueries, slot, value)
    },
    visibleAlternatives (slot) {
      const query = this.normalizeSearch(this.slotSearchQuery(slot.slot))
      const alternatives = query
        ? slot.alternatives.filter(entry => this.normalizeSearch(entry.perk.displayName).indexOf(query) !== -1)
        : slot.alternatives
      return alternatives.slice(0, 12)
    },
    normalizeSearch (value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },
    slotLabel (slot) {
      if (slot.slot === 1) return this.$t('forge.build.centralSlot')
      if (slot.roles.length) return this.roleLabel(slot.roles[0])
      return [this.$t('forge.build.opening'), this.$t('forge.build.pressure'), this.$t('forge.build.chase'), this.$t('forge.build.closer')][slot.slot - 1]
    },
    roleLabel (role) {
      const key = `forge.roles.${role}`
      return this.$te(key) ? this.$t(key) : String(role || '').replace(/_/g, ' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.build-lab-page { color: #f4f3ed; }
.eyebrow { color: #68dcff; font-size: 11px; font-weight: 900; letter-spacing: .16em; text-transform: uppercase; }
.build-lab-intro { margin-bottom: 14px; padding: 28px 32px; border: 1px solid rgba(255,255,255,.09); border-radius: 26px; background: linear-gradient(135deg, rgba(20,27,32,.96), rgba(8,11,14,.95)); display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; }
.build-lab-intro h1 { margin: 6px 0 8px; font-size: clamp(38px, 5vw, 66px); letter-spacing: -.06em; line-height: .95; }
.build-lab-intro p { max-width: 720px; margin: 0; color: rgba(255,255,255,.58); font-size: 14px; line-height: 1.6; }
.build-side-switch { padding: 4px; border-radius: 14px; background: #070a0c; display: flex; }
.build-side-switch button { min-height: 44px; padding: 0 18px; border: 1px solid transparent; border-radius: 10px; background: transparent; color: rgba(255,255,255,.45); cursor: pointer; font-size: 10px; font-weight: 900; text-transform: uppercase; }
.build-side-switch button.is-active { border-color: rgba(104,220,255,.35); background: rgba(104,220,255,.12); color: #fff; }
.killer-build-lab { padding: 18px; border: 1px solid rgba(255,255,255,.08); border-radius: 26px; background: rgba(8,11,14,.94); }
.killer-build-controls { display: grid; grid-template-columns: minmax(210px,.7fr) minmax(390px,1.4fr) auto; gap: 12px; align-items: end; }
.killer-build-controls label > span, .central-perk-control > span { display: block; margin-bottom: 6px; color: rgba(255,255,255,.48); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; }
.killer-build-controls select, .central-perk-control select { width: 100%; min-height: 58px; padding: 0 14px; border: 1px solid rgba(255,255,255,.12); border-radius: 13px; background: #0d1215; color: #fff; font-size: 14px; }
.strategy-switch { padding: 4px; border-radius: 14px; background: #050708; display: grid; grid-template-columns: repeat(3,1fr); gap: 4px; }
.strategy-switch button, .mode-switch button { min-height: 50px; border: 1px solid transparent; border-radius: 10px; background: transparent; color: rgba(255,255,255,.45); cursor: pointer; }
.strategy-switch button.is-active { border-color: rgba(255,127,109,.34); background: rgba(255,127,109,.11); color: #fff; }
.strategy-switch small, .mode-switch small { display: block; font-size: 9px; letter-spacing: .1em; text-transform: uppercase; }
.strategy-switch strong, .mode-switch strong { font-size: 12px; }
.random-killer, .toolbar-button { min-height: 58px; padding: 0 18px; border: 0; border-radius: 13px; background: #f2eee4; color: #111; cursor: pointer; font-size: 11px; font-weight: 900; text-transform: uppercase; }
.killer-builder-toolbar { margin-top: 12px; padding: 10px; border: 1px solid rgba(104,220,255,.1); border-radius: 17px; background: linear-gradient(90deg,rgba(104,220,255,.055),rgba(255,255,255,.018)); display: grid; grid-template-columns: minmax(250px,1fr) auto minmax(320px,.9fr) auto; gap: 9px; align-items: end; }
.mode-switch { padding: 4px; border-radius: 13px; background: rgba(2,5,7,.78); display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
.mode-switch button.is-active { border-color: rgba(104,220,255,.4); background: rgba(104,220,255,.11); color: #fff; }
.toolbar-button { min-height: 50px; border: 1px solid rgba(104,220,255,.24); background: rgba(104,220,255,.07); color: #a9efff; }
.toolbar-button--reset { border-color: rgba(255,255,255,.12); background: transparent; color: rgba(255,255,255,.62); }
.killer-gameplan-picker { margin-top: 10px; padding: 13px; border: 1px solid rgba(255,194,87,.13); border-radius: 17px; background: linear-gradient(100deg,rgba(255,194,87,.045),rgba(255,255,255,.012)); }
.killer-gameplan-picker > header { margin-bottom: 10px; display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; }
.killer-gameplan-picker > header strong { margin-top: 3px; color: #fff; display: block; font-size: 15px; }
.killer-gameplan-picker > header p { max-width: 680px; margin: 0; color: rgba(255,255,255,.48); font-size: 11px; line-height: 1.45; text-align: right; }
.killer-gameplan-picker > div { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 7px; }
.killer-gameplan-picker button { min-width: 0; min-height: 58px; padding: 9px 11px; border: 1px solid rgba(255,255,255,.08); border-radius: 11px; background: rgba(3,6,8,.58); color: rgba(255,255,255,.52); cursor: pointer; text-align: left; }
.killer-gameplan-picker button strong, .killer-gameplan-picker button small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.killer-gameplan-picker button strong { font-size: 11px; }
.killer-gameplan-picker button small { margin-top: 5px; color: rgba(255,255,255,.32); font-size: 8px; letter-spacing: .07em; text-transform: uppercase; }
.killer-gameplan-picker button:hover, .killer-gameplan-picker button.is-active { border-color: rgba(255,194,87,.42); background: rgba(255,194,87,.09); color: #fff; }
.killer-gameplan-picker button.is-active small { color: #ffd16f; }
.killer-action-hub { margin-top: 10px; padding: 11px; border: 1px solid rgba(255,255,255,.07); border-radius: 17px; background: #070a0c; display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 12px; align-items: center; }
.killer-recommendations { min-width: 0; display: grid; grid-template-columns: minmax(170px,.7fr) repeat(4,minmax(105px,1fr)); gap: 7px; align-items: stretch; }
.killer-recommendations > div { padding: 6px 8px; }
.killer-recommendations > div small { margin-top: 4px; color: rgba(255,255,255,.45); display: block; font-size: 10px; line-height: 1.35; }
.killer-recommendations button { min-width: 0; min-height: 48px; padding: 8px 10px; border: 1px solid rgba(255,255,255,.09); border-radius: 10px; background: rgba(255,255,255,.025); color: rgba(255,255,255,.65); cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; text-align: left; }
.killer-recommendations button:hover, .killer-recommendations button.is-active { border-color: rgba(255,194,87,.42); background: rgba(255,194,87,.08); color: #fff; }
.killer-recommendations button span { min-width: 0; font-size: 11px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.killer-recommendations button strong { color: #82e8ba; font-size: 12px; }
.killer-quick-actions { display: grid; grid-template-columns: repeat(3,auto); gap: 6px; }
.killer-quick-actions button { min-height: 42px; padding: 0 12px; border: 1px solid rgba(104,220,255,.2); border-radius: 10px; background: rgba(104,220,255,.06); color: #a9efff; cursor: pointer; font-size: 10px; font-weight: 900; text-transform: uppercase; }
.killer-quick-actions button.is-primary { border-color: #f2eee4; background: #f2eee4; color: #111; }
.killer-build-summary { margin-top: 13px; min-height: 150px; padding: 20px; border-radius: 18px; background: radial-gradient(circle at 20% 30%,rgba(104,220,255,.09),transparent 25%),linear-gradient(120deg,#151c21,#0b0f12); display: grid; grid-template-columns: minmax(290px,.9fr) auto minmax(300px,1fr); align-items: center; gap: 28px; }
.killer-build-summary__identity { display: flex; align-items: center; gap: 16px; }
.killer-monogram { width: 82px; height: 104px; border: 1px solid color-mix(in srgb,var(--killer-accent) 56%,transparent); border-radius: 18px; background: radial-gradient(circle at 50% 42%,color-mix(in srgb,var(--killer-accent) 18%,transparent),transparent 64%); display: grid; place-items: center; font-size: 26px; font-weight: 950; overflow: hidden; }
.killer-monogram img { width: 100%; height: 100%; object-fit: contain; object-position: center bottom; filter: drop-shadow(0 8px 12px rgba(0,0,0,.55)); }
.killer-build-summary small { color: var(--killer-accent,#68dcff); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; }
.killer-build-summary h2 { margin: 3px 0; font-size: 27px; letter-spacing: -.04em; }
.killer-build-summary p { margin: 0; color: rgba(255,255,255,.58); font-size: 13px; line-height: 1.6; }
.killer-build-summary__score { display: flex; align-items: baseline; }
.killer-build-summary__score span { margin-right: 10px; color: rgba(255,255,255,.42); font-size: 9px; text-transform: uppercase; }
.killer-build-summary__score strong { color: #82e8ba; font-size: 46px; }
.killer-build-summary__score small { color: rgba(255,255,255,.35); }
.killer-build-summary__tradeoff { display: block; margin-top: 8px; color: rgba(255,255,255,.48) !important; font-size: 9px !important; letter-spacing: .04em !important; line-height: 1.4; }
.summary-chips { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; }
.summary-chips span { padding: 5px 8px; border: 1px solid rgba(104,220,255,.16); border-radius: 999px; color: rgba(188,239,255,.76); font-size: 9px; font-weight: 800; text-transform: uppercase; }
.killer-build-grid { margin-top: 12px; display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 10px; }
.killer-build-card { min-width: 0; padding: 14px; border: 1px solid rgba(255,255,255,.09); border-radius: 18px; background: linear-gradient(160deg,rgba(255,255,255,.045),rgba(255,255,255,.015)); position: relative; overflow: hidden; }
.killer-build-card.is-central { border-color: rgba(255,190,91,.42); background: linear-gradient(160deg,rgba(255,190,91,.075),rgba(255,255,255,.015)); }
.killer-build-card.is-locked { border-color: rgba(130,232,186,.36); }
.killer-build-card__number { top: 13px; left: 14px; color: rgba(255,255,255,.12); font-size: 27px; font-weight: 950; position: absolute; }
.killer-build-card__badges { min-height: 30px; display: flex; justify-content: flex-end; gap: 6px; position: relative; z-index: 3; }
.killer-build-card__badges > span, .killer-build-card__badges button { padding: 5px 8px; border: 1px solid rgba(104,220,255,.2); border-radius: 999px; background: rgba(5,9,12,.78); color: #7fdfff; font-size: 9px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
.killer-build-card.is-central .killer-build-card__badges > span { border-color: rgba(255,194,87,.5); background: rgba(255,194,87,.12); color: #ffd16f; box-shadow: 0 0 18px rgba(255,194,87,.08); }
.killer-build-card__badges button { border-color: rgba(255,255,255,.12); color: rgba(255,255,255,.5); cursor: pointer; }
.killer-build-card__badges button.is-active { border-color: rgba(130,232,186,.42); color: #82e8ba; }
.killer-build-card__icon { height: 190px; margin-top: -4px; display: grid; place-items: center; overflow: hidden; }
.killer-build-card__icon > div { transform: scale(1.02); transform-origin: center; }
.killer-build-card__body h3 { min-height: 43px; margin: 3px 0 6px; color: #fff; font-size: 17px; line-height: 1.18; }
.killer-build-card__body > p { min-height: 30px; margin: 0; color: rgba(255,255,255,.5); font-size: 11px; line-height: 1.45; text-transform: capitalize; }
.killer-build-card__reason { min-height: 58px; margin-top: 10px; padding: 9px 10px; border: 1px solid rgba(255,255,255,.065); border-radius: 10px; background: rgba(2,5,7,.42); }
.killer-build-card__reason span { color: rgba(255,255,255,.38); display: block; font-size: 9px; font-weight: 900; letter-spacing: .09em; text-transform: uppercase; }
.killer-build-card__reason strong { margin-top: 5px; color: rgba(255,255,255,.75); display: block; font-size: 11px; font-weight: 650; line-height: 1.4; }
.killer-build-card__fit { margin-top: 10px; display: flex; align-items: baseline; justify-content: space-between; }
.killer-build-card__fit span { color: rgba(255,255,255,.4); font-size: 9px; letter-spacing: .1em; text-transform: uppercase; }
.killer-build-card__fit strong { color: #82e8ba; font-size: 16px; }
.killer-build-card__meter { height: 4px; margin-top: 5px; border-radius: 99px; background: rgba(255,255,255,.08); overflow: hidden; }
.killer-build-card__meter span { height: 100%; background: linear-gradient(90deg,#68dcff,#82e8ba); display: block; }
.quick-swaps { margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.07); display: grid; gap: 5px; }
.quick-swaps > span { color: rgba(255,255,255,.4); font-size: 9px; font-weight: 900; letter-spacing: .09em; text-transform: uppercase; }
.quick-swaps button, .alternative-list button { min-height: 31px; padding: 0 8px; border: 1px solid rgba(255,255,255,.075); border-radius: 8px; background: rgba(255,255,255,.025); color: rgba(255,255,255,.68); cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; text-align: left; }
.quick-swaps button:hover, .alternative-list button:hover { border-color: rgba(104,220,255,.35); color: #fff; }
.quick-swaps button span, .alternative-list button span { min-width: 0; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.quick-swaps button strong, .alternative-list strong { color: #82e8ba; font-size: 10px; }
.killer-build-card details { margin-top: 13px; border-top: 1px solid rgba(255,255,255,.08); padding-top: 10px; }
.killer-build-card summary { color: rgba(255,255,255,.6); cursor: pointer; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.killer-build-card summary b { color: #68dcff; float: right; }
.alternative-search { margin-top: 10px; display: block; }
.alternative-search span { display: block; margin-bottom: 5px; color: rgba(255,255,255,.4); font-size: 9px; text-transform: uppercase; }
.alternative-search input { width: 100%; min-height: 34px; padding: 0 9px; border: 1px solid rgba(104,220,255,.18); border-radius: 8px; background: #070a0c; color: #fff; }
.alternative-list { max-height: 190px; margin-top: 7px; padding-right: 3px; display: grid; gap: 5px; overflow-y: auto; }
.killer-build-flow { margin-top: 10px; padding: 9px; border: 1px solid rgba(104,220,255,.1); border-radius: 15px; background: rgba(104,220,255,.03); display: grid; grid-template-columns: repeat(4,1fr); gap: 18px; }
.killer-build-flow article { min-width: 0; padding: 9px 11px; position: relative; }
.killer-build-flow article:not(:last-child)::after { content: '›'; color: #68dcff; font-size: 22px; right: -13px; top: 50%; position: absolute; transform: translateY(-50%); }
.killer-build-flow small, .killer-build-flow span { color: rgba(255,255,255,.42); display: block; font-size: 9px; text-transform: uppercase; }
.killer-build-flow strong { margin: 4px 0; color: rgba(255,255,255,.92); display: block; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.killer-build-flow span { color: #82e8ba; }
.killer-diagnostics, .killer-build-debrief { margin-top: 12px; padding: 17px; border: 1px solid rgba(104,220,255,.12); border-radius: 18px; background: linear-gradient(135deg,rgba(104,220,255,.045),rgba(255,255,255,.012)); }
.killer-build-debrief { border-color: rgba(255,255,255,.075); background: #070a0c; }
.killer-diagnostics > header, .killer-build-debrief > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; }
.killer-diagnostics h3, .killer-build-debrief h3 { margin: 4px 0 0; font-size: 18px; }
.killer-diagnostics > header p { max-width: 600px; margin: 0; color: rgba(255,255,255,.5); font-size: 11px; line-height: 1.5; text-align: right; }
.diagnostic-grid, .killer-build-debrief > div { margin-top: 13px; display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
.diagnostic-grid article, .killer-build-debrief article { min-width: 0; padding: 12px; border: 1px solid rgba(255,255,255,.07); border-radius: 12px; background: rgba(3,7,9,.65); }
.diagnostic-grid article > div:first-child { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.diagnostic-grid article > div:first-child span { color: rgba(255,255,255,.58); font-size: 10px; font-weight: 850; text-transform: uppercase; }
.diagnostic-grid article > div:first-child strong, .killer-build-debrief > header > strong { color: #82e8ba; font-size: 19px; }
.diagnostic-meter { height: 5px; margin: 8px 0; border-radius: 99px; background: rgba(255,255,255,.08); overflow: hidden; }
.diagnostic-meter span { height: 100%; background: linear-gradient(90deg,#68dcff,#82e8ba); display: block; }
.diagnostic-grid small { color: rgba(255,255,255,.4); font-size: 10px; line-height: 1.4; }
.killer-build-debrief article > small { color: #68dcff; font-size: 9px; font-weight: 900; text-transform: uppercase; }
.killer-build-debrief h4 { min-height: 38px; margin: 6px 0; font-size: 14px; }
.killer-build-debrief p { min-height: 48px; margin: 0; color: rgba(255,255,255,.58); font-size: 11px; line-height: 1.45; }
.killer-build-debrief article > span { margin-top: 9px; color: rgba(255,255,255,.34); display: block; font-size: 9px; line-height: 1.4; }
.killer-analysis-grid { margin-top: 12px; display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
.killer-analysis-grid article { padding: 18px; border: 1px solid rgba(255,255,255,.07); border-radius: 16px; background: #070a0c; }
.killer-analysis-grid article.has-warning { border-color: rgba(255,146,127,.26); }
.killer-analysis-grid h3 { margin: 5px 0 8px; font-size: 15px; text-transform: capitalize; }
.killer-analysis-grid p { margin: 0; color: rgba(255,255,255,.5); font-size: 11px; line-height: 1.55; }
@media (max-width:1280px) { .killer-action-hub { grid-template-columns: 1fr; } .killer-quick-actions { grid-template-columns: repeat(3,1fr); } }
@media (max-width:1180px) { .killer-builder-toolbar { grid-template-columns: minmax(240px,1fr) auto minmax(300px,1fr); } .toolbar-button--reset { grid-column: 1/-1; } .killer-build-card__icon > div { transform: scale(.92); } }
@media (max-width:1040px) { .killer-build-controls { grid-template-columns: 1fr 1.4fr; } .random-killer { grid-column: 1/-1; } .killer-builder-toolbar { grid-template-columns: 1fr 1fr; } .toolbar-button--reset { grid-column: auto; } .killer-gameplan-picker > div { grid-template-columns: repeat(2,minmax(0,1fr)); } .killer-recommendations { grid-template-columns: repeat(2,1fr); } .killer-recommendations > div { grid-column: 1/-1; } .killer-build-summary { grid-template-columns: 1fr auto; } .killer-build-summary__copy { grid-column: 1/-1; } .killer-build-grid, .diagnostic-grid, .killer-build-debrief > div { grid-template-columns: repeat(2,1fr); } .killer-build-card__icon > div { transform: scale(1); } }
@media (max-width:700px) { .build-lab-intro { padding: 24px 20px; align-items: stretch; flex-direction: column; } .build-side-switch button { flex: 1; } .killer-build-controls, .killer-builder-toolbar, .killer-gameplan-picker > div, .killer-recommendations, .killer-quick-actions, .killer-build-flow, .diagnostic-grid, .killer-build-debrief > div, .killer-analysis-grid { grid-template-columns: 1fr; } .killer-gameplan-picker > header { align-items: flex-start; flex-direction: column; } .killer-gameplan-picker > header p { text-align: left; } .random-killer, .toolbar-button--reset, .killer-recommendations > div { grid-column: auto; } .killer-build-summary { grid-template-columns: 1fr; } .killer-build-summary__score { justify-self: start; } .killer-build-flow article::after { display: none; } .killer-diagnostics > header, .killer-build-debrief > header { align-items: flex-start; flex-direction: column; } .killer-diagnostics > header p { text-align: left; } }
@media (max-width:520px) { .killer-build-grid { grid-template-columns: 1fr; } .killer-build-card__icon { height: 178px; } }
@media (max-width:460px) { .killer-build-lab { padding: 11px; } .strategy-switch { grid-template-columns: 1fr; } }
</style>
